/**
 * Payments — CPACharge integration dashboard
 * Redesigned: current-month KPIs only, dollar amounts, recurring vs one-time,
 * success rate (completed ÷ attempted), plus two detail tables.
 */

import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Search,
  CreditCard,
  Building2,
  ChevronLeft,
  ChevronRight,
  Repeat,
  Zap,
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function TypeBadge({ type }: { type: "Recurring" | "One-Time" | string }) {
  if (type === "Recurring")
    return (
      <Badge className="bg-[oklch(0.90_0.06_280)] text-[oklch(0.28_0.10_280)] border border-[oklch(0.78_0.06_280)] gap-1 text-xs">
        <Repeat className="w-3 h-3" /> Recurring
      </Badge>
    );
  return (
    <Badge className="bg-[oklch(0.90_0.06_200)] text-[oklch(0.28_0.08_200)] border border-[oklch(0.78_0.06_200)] gap-1 text-xs">
      <Zap className="w-3 h-3" /> One-Time
    </Badge>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  accent: string;   // OKLCH border/icon color
  bg: string;       // OKLCH card bg
  isLoading: boolean;
}

function KpiCard({ label, value, sub, icon: Icon, accent, bg, isLoading }: KpiCardProps) {
  return (
    <Card
      className="border"
      style={{ background: bg, borderColor: `${accent}40` }}
    >
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-[oklch(0.35_0.03_240)]">
            {label}
          </p>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
        {isLoading ? (
          <div className="h-8 w-28 bg-[oklch(0.92_0.008_240)] rounded animate-pulse" />
        ) : (
          <p className="text-2xl font-bold text-[oklch(0.12_0.04_240)]">{value}</p>
        )}
        {sub && (
          <p className="text-xs text-[oklch(0.35_0.03_240)] mt-1">{sub}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Detail Table ─────────────────────────────────────────────────────────────

interface DetailRow {
  id: string;
  date: string;
  clientName: string;
  amount: number;
  type: string;
  method: string;
  status: string;
}

function DetailTable({
  title,
  rows,
  isLoading,
  variant,
}: {
  title: string;
  rows: DetailRow[];
  isLoading: boolean;
  variant: "completed" | "failed";
}) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.clientName.toLowerCase().includes(q) ||
        r.date.includes(q) ||
        String(r.amount).includes(q) ||
        r.type.toLowerCase().includes(q) ||
        r.method.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const headerBg = variant === "completed"
    ? "oklch(0.90_0.06_145)"
    : "oklch(0.90_0.06_25)";
  const headerText = variant === "completed"
    ? "oklch(0.25_0.08_145)"
    : "oklch(0.35_0.12_25)";
  const countBg = variant === "completed"
    ? "bg-[oklch(0.88_0.08_145)] text-[oklch(0.25_0.08_145)] border-[oklch(0.75_0.08_145)]"
    : "bg-[oklch(0.92_0.08_25)] text-[oklch(0.35_0.12_25)] border-[oklch(0.78_0.08_25)]";

  return (
    <Card className="bg-[oklch(0.97_0.005_240)] border border-[oklch(0.88_0.01_240)] flex flex-col">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            {variant === "completed"
              ? <CheckCircle2 className="w-4 h-4" style={{ color: "oklch(0.50_0.12_145)" }} />
              : <XCircle className="w-4 h-4" style={{ color: "oklch(0.50_0.12_25)" }} />
            }
            <CardTitle className="text-sm font-semibold uppercase tracking-widest"
              style={{ color: headerText }}>
              {title}
            </CardTitle>
            {!isLoading && (
              <Badge className={`border text-xs ${countBg}`}>
                {rows.length}
              </Badge>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2 w-3 h-3 text-[oklch(0.40_0.02_240)]" />
            <Input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 h-7 w-36 text-xs bg-white border-[oklch(0.88_0.01_240)]"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-9 bg-[oklch(0.93_0.008_240)] rounded animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-[oklch(0.35_0.02_240)]">
            {variant === "completed"
              ? <CheckCircle2 className="w-8 h-8 opacity-30" />
              : <XCircle className="w-8 h-8 opacity-30" />
            }
            <p className="text-sm">
              {rows.length === 0
                ? `No ${variant} payments this month`
                : "No results match your search"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow
                  className="border-[oklch(0.88_0.01_240)] hover:bg-transparent"
                  style={{ background: `${headerBg}30` }}
                >
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)] py-2">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)] py-2">Client</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)] py-2 text-right">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)] py-2">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)] py-2">Method</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow
                    key={row.id}
                    className="border-[oklch(0.92_0.008_240)] hover:bg-[oklch(0.94_0.018_220)] transition-colors"
                  >
                    <TableCell className="text-xs text-[oklch(0.25_0.03_240)] whitespace-nowrap py-2">
                      {formatDate(row.date)}
                    </TableCell>
                    <TableCell className="text-xs font-medium text-[oklch(0.18_0.04_240)] py-2 max-w-[160px] truncate">
                      {row.clientName}
                    </TableCell>
                    <TableCell className="text-xs font-semibold text-right py-2"
                      style={{ color: variant === "completed" ? "oklch(0.30_0.10_145)" : "oklch(0.40_0.12_25)" }}>
                      {formatCurrency(row.amount)}
                    </TableCell>
                    <TableCell className="py-2">
                      <TypeBadge type={row.type} />
                    </TableCell>
                    <TableCell className="text-xs text-[oklch(0.30_0.03_240)] py-2">
                      {row.method || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

export default function Payments() {
  const { user } = useAuth();
  const MARIAN_EMAIL = "marian@apxsystem.io";
  const hasAccess =
    !user ||
    user.role !== "bookkeeper" ||
    user.email?.toLowerCase() === MARIAN_EMAIL;

  const [historyPage, setHistoryPage] = useState(1);
  const [historySearch, setHistorySearch] = useState("");
  // Month picker: default to current month as "YYYY-MM"
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  });
  const isCurrentMonth = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    return selectedMonth === `${y}-${m}`;
  })();
  const navigateMonth = (delta: number) => {
    const [y, m] = selectedMonth.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    const ny = d.getUTCFullYear();
    const nm = String(d.getUTCMonth() + 1).padStart(2, "0");
    // Don't allow future months
    const now = new Date();
    const nowYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const next = `${ny}-${nm}`;
    if (next <= nowYM) setSelectedMonth(next);
  };

  if (!hasAccess) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="text-5xl">🔒</div>
          <h2 className="text-xl font-semibold text-[oklch(0.18_0.04_240)]"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Access Restricted
          </h2>
          <p className="text-sm text-[oklch(0.35_0.03_240)]">
            You do not have permission to view the Payments dashboard.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const { data, isLoading, isFetching, refetch } = trpc.cpaCharge.getPaymentsDashboard.useQuery({
    page: historyPage,
    pageSize: PAGE_SIZE,
    selectedMonth,
  });

  const invalidateCache = trpc.cpaCharge.invalidateCache.useMutation({
    onSuccess: () => refetch(),
  });

  const isRefreshing = isFetching || invalidateCache.isPending;
  const cm = data?.currentMonth;
  const merchantInfo = data?.merchantInfo;
  const summary = data?.summary;

  // History table with client-side search
  const historyRows = useMemo(() => {
    const results = data?.transactions?.results ?? [];
    if (!historySearch.trim()) return results;
    const q = historySearch.toLowerCase();
    return results.filter(
      (tx: any) =>
        (tx.method?.name ?? "").toLowerCase().includes(q) ||
        (tx.reference ?? "").toLowerCase().includes(q) ||
        String(tx.amount).includes(q)
    );
  }, [data?.transactions?.results, historySearch]);

  const totalHistoryPages = data
    ? Math.ceil((data.transactions?.total ?? 0) / PAGE_SIZE)
    : 1;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">

        {/* ── Header ── */}
        <div className="space-y-1">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight text-[oklch(0.12_0.04_240)]"
                style={{ fontFamily: "'Playfair Display', serif" }}>
                Payments
              </h1>
              <Badge
                className="text-xs font-semibold"
                style={{
                  background: "oklch(0.93_0.008_240)",
                  color: "oklch(0.35_0.03_240)",
                  border: "1px solid oklch(0.82_0.01_240)",
                }}
              >
                CPACharge
              </Badge>
              {/* Month navigator */}
              <div className="flex items-center gap-1 rounded-lg border border-[oklch(0.82_0.01_240)] bg-[oklch(0.96_0.01_240)] px-1 py-0.5">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-[oklch(0.90_0.02_240)] transition-colors text-[oklch(0.35_0.03_240)] hover:text-[oklch(0.12_0.04_240)]"
                  title="Previous month"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span
                  className="text-sm font-semibold px-1 min-w-[130px] text-center"
                  style={{ color: "oklch(0.25_0.08_230)" }}
                >
                  {cm ? cm.label : selectedMonth}
                  {isCurrentMonth && (
                    <span className="ml-1.5 text-xs font-normal text-[oklch(0.45_0.03_240)]">· Current</span>
                  )}
                </span>
                <button
                  onClick={() => navigateMonth(1)}
                  disabled={isCurrentMonth}
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-[oklch(0.90_0.02_240)] transition-colors text-[oklch(0.35_0.03_240)] hover:text-[oklch(0.12_0.04_240)] disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Next month"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-sm text-[oklch(0.35_0.03_240)] hover:text-[oklch(0.18_0.04_240)] gap-1.5"
              onClick={() => invalidateCache.mutate()}
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
              {isRefreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </div>

          {/* Merchant info bar */}
          <div className="flex items-center gap-3 flex-wrap text-xs text-[oklch(0.35_0.03_240)]">
            {merchantInfo && (
              <>
                <span className="flex items-center gap-1">
                  <Building2 className="w-3 h-3 text-[oklch(0.55_0.10_75)]" />
                  <span className="text-[oklch(0.28_0.04_240)] font-medium">{merchantInfo.name}</span>
                </span>
                {(merchantInfo as any).contact_email && (
                  <><span>·</span><span>{(merchantInfo as any).contact_email}</span></>
                )}
                <span>·</span>
              </>
            )}
            {summary && (
              <span className="flex items-center gap-1">
                <CreditCard className="w-3 h-3 text-[oklch(0.55_0.10_75)]" />
                <span>{summary.totalTransactions.toLocaleString()} transactions on file</span>
              </span>
            )}
            {data?.cachedAt && (
              <span>Cached · refreshes every 5 min</span>
            )}
            <span className="flex items-center gap-1 ml-auto">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[oklch(0.55_0.14_145)] animate-pulse" />
              <span>Live</span>
            </span>
          </div>
        </div>

        {/* ── 4 KPI Cards — Current Month ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Recurring Completed"
            value={cm ? formatCurrency(cm.totalRecurringCompleted) : "—"}
            sub={cm ? `${cm.recurringCompletedCount} scheduled charges` : undefined}
            icon={Repeat}
            accent="oklch(0.50_0.14_280)"
            bg="oklch(0.96_0.02_280)"
            isLoading={isLoading}
          />
          <KpiCard
            label="One-Time Completed"
            value={cm ? formatCurrency(cm.totalOneTimeCompleted) : "—"}
            sub={cm ? `${cm.oneTimeCompletedCount} one-time payments` : undefined}
            icon={Zap}
            accent="oklch(0.50_0.12_200)"
            bg="oklch(0.96_0.02_200)"
            isLoading={isLoading}
          />
          <KpiCard
            label="Total Failed"
            value={cm ? formatCurrency(cm.totalFailedAmount) : "—"}
            sub={cm ? `${cm.failedCount} failed (${cm.recurringFailedCount} recurring · ${cm.oneTimeFailedCount} one-time)` : undefined}
            icon={AlertTriangle}
            accent="oklch(0.50_0.14_25)"
            bg="oklch(0.97_0.02_25)"
            isLoading={isLoading}
          />
          <KpiCard
            label="% Completed"
            value={cm ? `${cm.successRate}%` : "—"}
            sub={cm ? `${cm.completedCount} completed of ${cm.attemptedCount} attempted` : undefined}
            icon={TrendingUp}
            accent="oklch(0.50_0.12_145)"
            bg="oklch(0.96_0.03_145)"
            isLoading={isLoading}
          />
        </div>

        {/* ── Two Detail Tables — Completed & Failed ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DetailTable
            title="Completed Payments"
            rows={cm?.completedTransactions ?? []}
            isLoading={isLoading}
            variant="completed"
          />
          <DetailTable
            title="Failed Payments"
            rows={cm?.failedTransactions ?? []}
            isLoading={isLoading}
            variant="failed"
          />
        </div>

        {/* ── Full Transaction History ── */}
        <Card className="bg-[oklch(0.97_0.005_240)] border border-[oklch(0.88_0.01_240)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-widest text-[oklch(0.35_0.03_240)]">
                Full Transaction History
              </CardTitle>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-[oklch(0.30_0.03_240)]" />
                <Input
                  placeholder="Search…"
                  value={historySearch}
                  onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
                  className="pl-8 h-8 w-44 text-sm bg-white border-[oklch(0.88_0.01_240)]"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-10 bg-[oklch(0.93_0.008_240)] rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[oklch(0.88_0.01_240)] hover:bg-transparent bg-[oklch(0.94_0.01_240)]">
                        <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)]">Date</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)]">Client / Payor</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)] text-right">Amount</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)]">Type</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)]">Status</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider font-semibold text-[oklch(0.35_0.03_240)]">Method</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-10 text-[oklch(0.30_0.03_240)] text-sm">
                            No transactions found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        historyRows.map((tx: any) => {
                          const isCompleted = tx.status === "COMPLETED" || tx.status === "PAID";
                          const isFailed = tx.status === "FAILED";
                          const isRecurring = !!tx.recurring_charge_id;
                          const payorName = tx.method?.name ?? tx.reference ?? "—";
                          const methodLabel = tx.method?.type === "bank"
                            ? `${tx.method.bank_name ?? "Bank"} ···${(tx.method.account_number ?? "").slice(-4)}`
                            : tx.method?.card_type
                              ? `${tx.method.card_type} ···${(tx.method.number ?? "").slice(-4)}`
                              : tx.method?.type ?? "—";
                          return (
                            <TableRow
                              key={tx.id}
                              className="border-[oklch(0.92_0.008_240)] hover:bg-[oklch(0.94_0.018_220)] transition-colors"
                            >
                              <TableCell className="text-xs text-[oklch(0.25_0.03_240)] whitespace-nowrap">
                                {formatDate(tx.created?.slice(0, 10))}
                              </TableCell>
                              <TableCell className="text-xs font-medium text-[oklch(0.18_0.04_240)] max-w-[180px] truncate">
                                {payorName}
                              </TableCell>
                              <TableCell className="text-xs font-semibold text-right"
                                style={{ color: isFailed ? "oklch(0.40_0.12_25)" : isCompleted ? "oklch(0.30_0.10_145)" : "oklch(0.25_0.03_240)" }}>
                                {formatCurrency(tx.amount)}
                              </TableCell>
                              <TableCell>
                                <TypeBadge type={isRecurring ? "Recurring" : "One-Time"} />
                              </TableCell>
                              <TableCell>
                                {isCompleted ? (
                                  <Badge className="bg-[oklch(0.88_0.08_145)] text-[oklch(0.25_0.08_145)] border border-[oklch(0.75_0.08_145)] gap-1 text-xs">
                                    <CheckCircle2 className="w-3 h-3" /> Completed
                                  </Badge>
                                ) : isFailed ? (
                                  <Badge className="bg-[oklch(0.92_0.08_25)] text-[oklch(0.35_0.12_25)] border border-[oklch(0.78_0.08_25)] gap-1 text-xs">
                                    <XCircle className="w-3 h-3" /> Failed
                                  </Badge>
                                ) : (
                                  <Badge className="bg-[oklch(0.93_0.06_75)] text-[oklch(0.35_0.10_75)] border border-[oklch(0.78_0.06_75)] gap-1 text-xs">
                                    <Clock className="w-3 h-3" /> {tx.status}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-[oklch(0.30_0.03_240)]">
                                {methodLabel}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalHistoryPages > 1 && (
                  <div className="flex items-center justify-between px-5 py-3 border-t border-[oklch(0.92_0.008_240)]">
                    <p className="text-xs text-[oklch(0.30_0.03_240)]">
                      Page {historyPage} of {totalHistoryPages} — {data?.transactions?.total ?? 0} total
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-[oklch(0.35_0.03_240)]"
                        onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                        disabled={historyPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-[oklch(0.35_0.03_240)]"
                        onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                        disabled={historyPage === totalHistoryPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
}
